import React, { useMemo } from "react";
import styled from "styled-components";
import { Coach as CoachModel } from "../model/Coach";
import { Division as DivisionModel, Week as WeekModel } from "../model/Season";
import { useSeasonCalculations } from "../hooks/useSeasonCalculations";
import { calculateExpectedScore } from "../utils/eloCalculations";

const MatchupsSection = styled.div`
    margin-top: 1em;
`;

const MatchupsTitle = styled.h3`
    margin-bottom: 1em;
    color: #333;
`;

const MatchupsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1em;
    margin-top: 1em;
    width: 100%;
    min-width: 0;

    @media (min-width: 1020px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;


const TeamSection = styled.div<{ $alignRight?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
    min-width: 0;
    text-align: ${props => props.$alignRight ? 'left' : 'right'};
    align-items: ${props => props.$alignRight ? 'flex-start' : 'flex-end'};
`;

const TeamHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    align-items: inherit;
    flex-shrink: 0;
`;

const TeamStatsSpacer = styled.div`
    flex: 1;
    min-height: 0.5em;
`;

const TeamStatsBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    align-items: inherit;
    flex-shrink: 0;
`;

const TeamName = styled.div<{ $isWinner?: boolean; $isLoser?: boolean; $isTie?: boolean }>`
    font-weight: ${props => props.$isWinner ? '700' : '600'};
    font-size: 1em;
    color: ${props => {
        if (props.$isWinner) return '#28a745';
        if (props.$isLoser) return '#6c757d';
        return '#333';
    }};
    opacity: ${props => props.$isLoser ? '0.7' : '1'};
`;

const CoachName = styled.div`
    font-style: italic;
    color: #666;
    font-size: 0.85em;
`;

const WinProbabilityBadge = styled.div<{ $isFavorite?: boolean }>`
    font-size: 0.9em;
    font-weight: 600;
    color: ${props => props.$isFavorite ? '#28a745' : '#495057'};
    margin-top: 0.2em;
`;

const TVValue = styled.span`
    font-weight: 500;
    color: #495057;
`;

const VersusDivider = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5em;
    color: #999;
    font-weight: 600;
    font-size: 0.9em;
    flex-shrink: 0;
    align-self: flex-start;
`;

const NoMatchupsMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: #666;
    font-style: italic;
`;

const WeekSelectorContainer = styled.div`
    margin-bottom: 1em;
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-wrap: wrap;
`;

const WeekSelectorLabel = styled.label`
    font-weight: 600;
    font-size: 0.9em;
    color: #495057;
`;

const WeekSelectorDropdown = styled.select`
    padding: 0.4em 0.8em;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
    font-family: inherit;
    
    &:hover {
        border-color: #adb5bd;
    }
    
    &:focus {
        outline: none;
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`;

const WeekPreviewBadge = styled.span`
    padding: 0.2em 0.6em;
    background-color: #fff3cd;
    color: #856404;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
    border: 1px solid #ffc107;
`;

const WeekCompleteBadge = styled.span`
    padding: 0.2em 0.6em;
    background-color: #d4edda;
    color: #155724;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
    border: 1px solid #28a745;
`;

const CopyButton = styled.button`
    padding: 0.4em 0.8em;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.9em;
    cursor: pointer;
    font-family: inherit;
    font-weight: 500;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: #0056b3;
    }
    
    &:active {
        background-color: #004085;
    }
    
    &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`;

const MatchupCard = styled.div<{ $isComplete?: boolean; $awayWon?: boolean; $homeWon?: boolean; $isTie?: boolean }>`
    background-color: ${props => {
        if (props.$isComplete) {
            return '#e8f5e9'; // Green background for all completed matches (wins and ties)
        }
        return 'white';
    }};
    border: 1px solid ${props => {
        if (props.$isComplete) {
            return '#28a745'; // Green border for all completed matches
        }
        return '#ddd';
    }};
    border-radius: 8px;
    padding: 1em;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    gap: 1em;
    position: relative;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    min-height: 140px;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    
    ${props => props.$isComplete && `
        &::after {
            content: '✓';
            position: absolute;
            bottom: 0.5em;
            left: 50%;
            transform: translateX(-50%);
            color: #28a745;
            font-weight: bold;
            font-size: 1.2em;
        }
    `}
`;

const MatchScore = styled.div<{ $isWinner?: boolean; $isLoser?: boolean; $isTie?: boolean }>`
    font-size: 1.1em;
    font-weight: 600;
    margin-top: 0.3em;
    color: ${props => {
        if (props.$isWinner) return '#28a745';
        if (props.$isLoser) return '#6c757d';
        if (props.$isTie) return '#495057';
        return '#333';
    }};
`;

interface ThisWeekMatchupsProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
    };
    leagueCoaches: CoachModel[];
    /** Shared week selection; when changed, all section dropdowns stay in sync. */
    selectedWeekIndex: number;
    onSelectedWeekChange: (index: number) => void;
}

export const ThisWeekMatchups: React.FC<ThisWeekMatchupsProps> = ({
    seasonData,
    leagueCoaches,
    selectedWeekIndex,
    onSelectedWeekChange
}) => {
    const { eloRatings } = useSeasonCalculations({ seasonData, leagueCoaches });

    // Helper function to check if a week is complete
    const isWeekComplete = (week: WeekModel | null | undefined): boolean => {
        if (!week || !Array.isArray(week.matches) || week.matches.length === 0) {
            return false;
        }
        return week.matches.every(match => 
            match && 
            match.results !== undefined && 
            match.results !== null &&
            match.results.homeTeamScore !== undefined &&
            match.results.awayTeamScore !== undefined
        );
    };

    const selectedWeek = seasonData.weeks[selectedWeekIndex] || null;

    // Get most recent TV for a team by looking through all match results
    // Memoize the TV cache to avoid recalculating on every render
    const tvCache = useMemo(() => {
        const cache: Record<string, number | null> = {};
        const weekIndices: Record<string, number> = {};

        // Search through all weeks and matches to find the most recent TV for each team
        seasonData.weeks.forEach((week, weekIndex) => {
            week.matches?.forEach(match => {
                if (!match.results) return;

                // Update cache for home team
                if (match.results.homeTeamTV !== undefined) {
                    const existingWeekIndex = weekIndices[match.homeTeamId];
                    if (existingWeekIndex === undefined || weekIndex > existingWeekIndex) {
                        cache[match.homeTeamId] = match.results.homeTeamTV;
                        weekIndices[match.homeTeamId] = weekIndex;
                    }
                }

                // Update cache for away team
                if (match.results.awayTeamTV !== undefined) {
                    const existingWeekIndex = weekIndices[match.awayTeamId];
                    if (existingWeekIndex === undefined || weekIndex > existingWeekIndex) {
                        cache[match.awayTeamId] = match.results.awayTeamTV;
                        weekIndices[match.awayTeamId] = weekIndex;
                    }
                }
            });
        });

        return cache;
    }, [seasonData.weeks]);

    const getMostRecentTV = (teamId: string): number | null => {
        return tvCache[teamId] ?? null;
    };

    // Cache team info (including coach names) to avoid repeated lookups
    const teamInfoCache = useMemo(() => {
        const cache: Record<string, { teamName: string; coachName: string; race: string }> = {};
        
        // Ensure leagueCoaches is an array
        const safeLeagueCoaches = Array.isArray(leagueCoaches) ? leagueCoaches : [];
        
        // Create a lookup map for coaches by coachId for faster access
        const coachesByCoachId = new Map(
            safeLeagueCoaches.map(coach => [coach.coachId, coach])
        );

        seasonData.divisions.forEach(division => {
            division.teams?.forEach(team => {
                if (team.teamId) {
                    const coach = coachesByCoachId.get(team.coachId);
                    cache[team.teamId] = {
                        teamName: team.teamName || 'Unknown Team',
                        coachName: coach?.coachName || `Coach ${team.coachId}`,
                        race: team.race || 'Unknown'
                    };
                }
            });
        });

        return cache;
    }, [seasonData.divisions, leagueCoaches]);

    // Get team and coach information from cache
    const getTeamInfo = (teamId: string) => {
        return teamInfoCache[teamId] || null;
    };

    // Get current ELO for a team
    const getCurrentElo = (teamId: string): number => {
        const rating = eloRatings.find(r => r.teamId === teamId);
        return rating?.currentElo ?? 1200; // Default to initial ELO if not found
    };

    // Render matchups for the selected week
    const matchups = useMemo(() => {
        if (!selectedWeek || !selectedWeek.matches) {
            return [];
        }

        return selectedWeek.matches
            .map(match => {
                const homeTeamInfo = getTeamInfo(match.homeTeamId);
                const awayTeamInfo = getTeamInfo(match.awayTeamId);

                if (!homeTeamInfo || !awayTeamInfo) {
                    return null;
                }

                const homeElo = getCurrentElo(match.homeTeamId);
                const awayElo = getCurrentElo(match.awayTeamId);

                // Calculate win probabilities (only for incomplete matches)
                const hasResults = match.results && 
                    match.results.homeTeamScore !== undefined && 
                    match.results.awayTeamScore !== undefined;
                
                const homeWinProbability = hasResults ? 0 : calculateExpectedScore(homeElo, awayElo);
                const awayWinProbability = hasResults ? 0 : calculateExpectedScore(awayElo, homeElo);

                const homeTV = getMostRecentTV(match.homeTeamId);
                const awayTV = getMostRecentTV(match.awayTeamId);

                // Determine match result
                let awayWon = false;
                let homeWon = false;
                let isTie = false;
                let awayScore = 0;
                let homeScore = 0;

                if (hasResults && match.results) {
                    awayScore = match.results.awayTeamScore ?? 0;
                    homeScore = match.results.homeTeamScore ?? 0;
                    if (awayScore > homeScore) {
                        awayWon = true;
                    } else if (homeScore > awayScore) {
                        homeWon = true;
                    } else {
                        isTie = true;
                    }
                }

                return {
                    match,
                    isComplete: hasResults,
                    awayWon,
                    homeWon,
                    isTie,
                    awayScore,
                    homeScore,
                    homeTeam: {
                        ...homeTeamInfo,
                        teamId: match.homeTeamId,
                        elo: homeElo,
                        winProbability: homeWinProbability,
                        tv: homeTV
                    },
                    awayTeam: {
                        ...awayTeamInfo,
                        teamId: match.awayTeamId,
                        elo: awayElo,
                        winProbability: awayWinProbability,
                        tv: awayTV
                    }
                };
            })
            .filter((matchup): matchup is NonNullable<typeof matchup> => matchup !== null);
    }, [selectedWeek, eloRatings, getMostRecentTV, teamInfoCache, seasonData.divisions]);

    // Get the selected week info for display
    const selectedWeekInfo = useMemo(() => {
        if (!selectedWeek) return null;
        const isComplete = isWeekComplete(selectedWeek);
        const completed = selectedWeek.matches?.filter(m => 
            m?.results && 
            m.results.homeTeamScore !== undefined && 
            m.results.awayTeamScore !== undefined
        ).length || 0;
        const total = selectedWeek.matches?.length || 0;
        return { week: selectedWeek, isComplete, completed, total };
    }, [selectedWeek]);

    // Format matchups for clipboard copy - uses the same matchups data displayed in cards
    const formatMatchupsForClipboard = (): string => {
        if (matchups.length === 0) return '';

        // Format each matchup using the exact same data shown in the cards
        return matchups.map(matchup => {
            // Use the coachName from the matchup data (same as what's displayed in cards)
            const awayStr = `${matchup.awayTeam.teamName} (${matchup.awayTeam.coachName})`;
            const homeStr = `${matchup.homeTeam.teamName} (${matchup.homeTeam.coachName})`;
            
            return `${awayStr} vs ${homeStr}`;
        }).join('\n');
    };

    // Handle copy to clipboard
    const handleCopyToClipboard = async () => {
        const formatted = formatMatchupsForClipboard();
        if (!formatted) return;

        try {
            await navigator.clipboard.writeText(formatted);
            // Could add a toast notification here if desired
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = formatted;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    if (!selectedWeek) {
        return (
            <MatchupsSection>
                <MatchupsTitle>This Week's Matchups</MatchupsTitle>
                <NoMatchupsMessage>
                    No week selected.
                </NoMatchupsMessage>
            </MatchupsSection>
        );
    }

    if (matchups.length === 0) {
        return (
            <MatchupsSection>
                <MatchupsTitle>This Week's Matchups</MatchupsTitle>
                <WeekSelectorContainer>
                    <WeekSelectorLabel htmlFor="week-selector">Week:</WeekSelectorLabel>
                    <WeekSelectorDropdown
                        id="week-selector"
                        value={selectedWeekIndex}
                        onChange={(e) => {
                            const newIndex = parseInt(e.target.value, 10);
                            if (!isNaN(newIndex) && newIndex >= 0) {
                                onSelectedWeekChange(newIndex);
                            }
                        }}
                    >
                        {seasonData.weeks.map((week, index) => {
                            const isComplete = isWeekComplete(week);
                            const completed = week.matches?.filter(m => 
                                m?.results && 
                                m.results.homeTeamScore !== undefined && 
                                m.results.awayTeamScore !== undefined
                            ).length || 0;
                            const total = week.matches?.length || 0;
                            return (
                                <option key={index} value={index}>
                                    {week.weekName} {isComplete ? '(Complete)' : `(${completed}/${total} matches)`}
                                </option>
                            );
                        })}
                    </WeekSelectorDropdown>
                    {selectedWeekInfo && (
                        <>
                            {selectedWeekInfo.isComplete ? (
                                <WeekCompleteBadge>Complete Week</WeekCompleteBadge>
                            ) : (
                                <WeekPreviewBadge>
                                    In-progress
                                </WeekPreviewBadge>
                            )}
                        </>
                    )}
                </WeekSelectorContainer>
                <NoMatchupsMessage>
                    No matchups found for this week.
                </NoMatchupsMessage>
            </MatchupsSection>
        );
    }

    return (
        <MatchupsSection>
            <WeekSelectorContainer>
                <WeekSelectorLabel htmlFor="week-selector">Week:</WeekSelectorLabel>
                <WeekSelectorDropdown
                    id="week-selector"
                    value={selectedWeekIndex}
                    onChange={(e) => {
                        const newIndex = parseInt(e.target.value, 10);
                        if (!isNaN(newIndex) && newIndex >= 0) {
                            onSelectedWeekChange(newIndex);
                        }
                    }}
                >
                    {seasonData.weeks.map((week, index) => {
                        const isComplete = isWeekComplete(week);
                        const completed = week.matches?.filter(m => 
                            m?.results && 
                            m.results.homeTeamScore !== undefined && 
                            m.results.awayTeamScore !== undefined
                        ).length || 0;
                        const total = week.matches?.length || 0;
                        return (
                            <option key={index} value={index}>
                                {week.weekName} {isComplete ? '(Complete)' : `(${completed}/${total} matches)`}
                            </option>
                        );
                    })}
                </WeekSelectorDropdown>
                {selectedWeekInfo && (
                    <>
                        {selectedWeekInfo.isComplete ? (
                            <WeekCompleteBadge>Complete Week</WeekCompleteBadge>
                        ) : (
                            <WeekPreviewBadge>
                                In-progress
                            </WeekPreviewBadge>
                        )}
                    </>
                )}
                {matchups.length > 0 && (
                    <CopyButton onClick={handleCopyToClipboard}>
                        📋 Copy Matchups
                    </CopyButton>
                )}
            </WeekSelectorContainer>
            <MatchupsContainer>
                {matchups.map((matchup, index) => {
                    const homeIsFavorite = matchup.homeTeam.winProbability > matchup.awayTeam.winProbability;
                    
                    return (
                        <MatchupCard 
                            key={`${matchup.match.homeTeamId}-${matchup.match.awayTeamId}-${index}`}
                            $isComplete={matchup.isComplete}
                            $awayWon={matchup.awayWon}
                            $homeWon={matchup.homeWon}
                            $isTie={matchup.isTie}
                        >
                            <TeamSection>
                                <TeamHeader>
                                    <TeamName 
                                        $isWinner={matchup.awayWon}
                                        $isLoser={matchup.homeWon}
                                        $isTie={matchup.isTie}
                                    >
                                        {matchup.awayTeam.teamName}
                                    </TeamName>
                                    <CoachName>{matchup.awayTeam.coachName}</CoachName>
                                </TeamHeader>
                                <TeamStatsSpacer />
                                <TeamStatsBlock>
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                        TV: <TVValue>{matchup.awayTeam.tv ?? '–'}</TVValue>
                                    </div>
                                    {matchup.isComplete ? (
                                        <MatchScore 
                                            $isWinner={matchup.awayWon}
                                            $isLoser={matchup.homeWon}
                                            $isTie={matchup.isTie}
                                        >
                                            {matchup.awayScore}
                                        </MatchScore>
                                    ) : (
                                        <WinProbabilityBadge $isFavorite={!homeIsFavorite}>
                                            {Math.round(matchup.awayTeam.winProbability * 100)}% win
                                        </WinProbabilityBadge>
                                    )}
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                        ELO: <TVValue>{matchup.awayTeam.elo}</TVValue>
                                    </div>
                                </TeamStatsBlock>
                            </TeamSection>

                            <VersusDivider>@</VersusDivider>

                            <TeamSection $alignRight>
                                <TeamHeader>
                                    <TeamName 
                                        $isWinner={matchup.homeWon}
                                        $isLoser={matchup.awayWon}
                                        $isTie={matchup.isTie}
                                    >
                                        {matchup.homeTeam.teamName}
                                    </TeamName>
                                    <CoachName>{matchup.homeTeam.coachName}</CoachName>
                                </TeamHeader>
                                <TeamStatsSpacer />
                                <TeamStatsBlock>
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                        TV: <TVValue>{matchup.homeTeam.tv ?? '–'}</TVValue>
                                    </div>
                                    {matchup.isComplete ? (
                                        <MatchScore 
                                            $isWinner={matchup.homeWon}
                                            $isLoser={matchup.awayWon}
                                            $isTie={matchup.isTie}
                                        >
                                            {matchup.homeScore}
                                        </MatchScore>
                                    ) : (
                                        <WinProbabilityBadge $isFavorite={homeIsFavorite}>
                                            {Math.round(matchup.homeTeam.winProbability * 100)}% win
                                        </WinProbabilityBadge>
                                    )}
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                        ELO: <TVValue>{matchup.homeTeam.elo}</TVValue>
                                    </div>
                                </TeamStatsBlock>
                            </TeamSection>
                        </MatchupCard>
                    );
                })}
            </MatchupsContainer>
        </MatchupsSection>
    );
};
